import { useCallback } from 'react';

import { useCountryScope, type AdminCountry } from '@/features/auth';

import { useSelectedCountryStore } from '../store/selected-country.store';

export interface SelectedCountry {
  /** Countries the staff member may work in. */
  countries: readonly AdminCountry[];
  /**
   * The selected country, always one of `countries`: the remembered choice
   * when it is still in scope, otherwise the first country. Null only when
   * no country is assigned.
   */
  country: AdminCountry | null;
  /** Ignores a country outside the scope. */
  select: (countryId: number) => void;
}

/**
 * The explicit Admin country for country-dependent screens. Pages pass
 * `country.id` to the Admin API, which re-checks it against the scope.
 */
export function useSelectedCountry(): SelectedCountry {
  const { countries } = useCountryScope();
  const storedId = useSelectedCountryStore((state) => state.countryId);
  const storeSelect = useSelectedCountryStore((state) => state.select);

  const country = countries.find((c) => c.id === storedId) ?? countries[0] ?? null;

  const select = useCallback(
    (countryId: number) => {
      if (countries.some((c) => c.id === countryId)) storeSelect(countryId);
    },
    [countries, storeSelect],
  );

  return { countries, country, select };
}

/** Display name in the given language, falling back to the other name, the code, then the id. */
export function countryName(country: AdminCountry, language: 'en' | 'ar' = 'en'): string {
  const preferred = language === 'ar' ? country.nameAr : country.nameEn;
  const other = language === 'ar' ? country.nameEn : country.nameAr;
  return preferred || other || country.code || String(country.id);
}
